// src/pages/seasons/SeasonSchedule.jsx
// Unified event schedule — games, practices, tournaments, meetings & more.
//
// Phase 3 — Unified event model:
//   • eventType: 'game' | 'practice' | 'tournament' | 'meeting' | 'other'
//   • Type-aware Add/Edit modal (conditional fields per type)
//   • Type-aware EventCard (colors, badges, actions)
//   • Filter chips (All | Games | Practices | …) — shown only if multiple types present
//   • "Take Attendance" quick-link on past event cards
//   • RSVP counts + nudge on ALL upcoming events (not just games)
//   • Season W/L/D record counts games only
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, addDoc, updateDoc,
  doc, getDoc, getDocs, serverTimestamp, writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import { useRole } from '@/context/RoleContext';
import { format, isPast } from 'date-fns';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import {
  ArrowLeftIcon,
  PlusIcon,
  PencilSquareIcon,
  XMarkIcon,
  MapPinIcon,
  TrophyIcon,
  CalendarDaysIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  BellAlertIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  FunnelIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/react/24/outline';
import GooglePlacesInput from '@/components/GooglePlacesInput';
import useNudgeCooldown from '@/hooks/useNudgeCooldown';
import NotifyComposerSlideOver from '@/components/messages/NotifyComposerSlideOver';
import TeamSubNav from '@/components/team/TeamSubNav';
import {
  PaperAirplaneIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

// Extract US ZIP code from a formatted address string (e.g. "..., CA 94132, USA" → "94132")
function extractZip(address) {
  return address?.match(/\b(\d{5})(?:-\d{4})?\b/)?.[1] || null;
}

/* ── Event type config ───────────────────────────────────────────────── */
const EVENT_TYPES = [
  {
    value: 'game',
    label: 'Game',
    emoji: '⚽',
    upcoming: { bg: 'bg-blue-50',    month: 'text-blue-500',    day: 'text-blue-700',    dow: 'text-blue-400'    },
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  {
    value: 'practice',
    label: 'Practice',
    emoji: '🏃',
    upcoming: { bg: 'bg-emerald-50', month: 'text-emerald-500', day: 'text-emerald-700', dow: 'text-emerald-400' },
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  {
    value: 'tournament',
    label: 'Tournament',
    emoji: '🏆',
    upcoming: { bg: 'bg-purple-50',  month: 'text-purple-500',  day: 'text-purple-700',  dow: 'text-purple-400'  },
    badge: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  {
    value: 'meeting',
    label: 'Meeting',
    emoji: '📋',
    upcoming: { bg: 'bg-amber-50',   month: 'text-amber-500',   day: 'text-amber-700',   dow: 'text-amber-400'   },
    badge: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  {
    value: 'other',
    label: 'Other',
    emoji: '📅',
    upcoming: { bg: 'bg-zinc-100',   month: 'text-zinc-500',    day: 'text-zinc-700',    dow: 'text-zinc-400'    },
    badge: 'bg-zinc-100 text-zinc-600 border-zinc-200',
  },
];

const PAST_COLORS      = { bg: 'bg-zinc-100', month: 'text-zinc-400', day: 'text-zinc-700', dow: 'text-zinc-400' };
const CANCELLED_COLORS = { bg: 'bg-zinc-100', month: 'text-zinc-300', day: 'text-zinc-400', dow: 'text-zinc-300' };

function getTypeInfo(eventType) {
  return EVENT_TYPES.find(t => t.value === (eventType || 'game')) || EVENT_TYPES[0];
}

function eventDisplayTitle(event) {
  if ((event.eventType || 'game') === 'game') return `vs ${event.opponent || 'TBD'}`;
  return event.title || getTypeInfo(event.eventType).label;
}

function sectionCount(n, filterType, t) {
  const tx = typeof t === 'function' ? t : (k) => k;
  if (filterType === 'all') {
    return tx('schedule.labels.eventCount', { count: n });
  }
  const typeLabel = tx(`schedule.eventType.${filterType}`).toLowerCase();
  return tx('schedule.labels.typeCount', { count: n, type: typeLabel });
}

/* ── Helpers ─────────────────────────────────────────────────────────── */
function toDate(v) {
  if (!v) return null;
  if (v?.toDate) return v.toDate();
  if (v?.seconds) return new Date(v.seconds * 1000);
  return new Date(v);
}
function fmtTime(d) { return d ? format(d, 'h:mm a') : ''; }

/* ── Week grouping for sticky-header schedule view ──────────────────── */

/** Monday-start week (US sports convention — most leagues run Mon-Sun). */
function startOfWeek(d) {
  const day = d.getDay(); // 0=Sun, 1=Mon, …, 6=Sat
  const offset = day === 0 ? -6 : 1 - day;
  const result = new Date(d);
  result.setDate(d.getDate() + offset);
  result.setHours(0, 0, 0, 0);
  return result;
}

function weekLabel(weekStart, today, t) {
  const tx = typeof t === 'function' ? t : (k) => k;
  const thisWeekStart = startOfWeek(today);
  const ms = (24 * 60 * 60 * 1000) * 7;
  const diffWeeks = Math.round((weekStart.getTime() - thisWeekStart.getTime()) / ms);
  if (diffWeeks === 0) return tx('schedule.weekLabel.thisWeek');
  if (diffWeeks === 1) return tx('schedule.weekLabel.nextWeek');
  if (diffWeeks < 0)   return null; // past — caller decides label
  // "Apr 28 — May 4" — keep concise
  const end = new Date(weekStart);
  end.setDate(weekStart.getDate() + 6);
  return `${format(weekStart, 'MMM d')} — ${format(end, 'MMM d')}`;
}

/**
 * Group an array of events into ordered week buckets. Returns
 * [{ weekStart: Date, label: string, events: [...] }, ...] sorted
 * earliest week first.
 */
function groupByWeek(events, t) {
  const groups = new Map();
  const today  = new Date();
  for (const e of events) {
    const d = toDate(e.date);
    if (!d) continue;
    const ws  = startOfWeek(d);
    const key = ws.getTime();
    if (!groups.has(key)) {
      groups.set(key, {
        weekStart: ws,
        label: weekLabel(ws, today, t),
        events: [],
      });
    }
    groups.get(key).events.push(e);
  }
  return Array.from(groups.values()).sort(
    (a, b) => a.weekStart.getTime() - b.weekStart.getTime()
  );
}

/**
 * Build a human toast summary from the sendGameUpdateSms callable response.
 * The backend fans out BOTH email (all parents with email) and SMS (opt-ins only),
 * so we surface both counts rather than the old SMS-only phrasing.
 *   { emailsSent: 7, smsSent: 3 } → "7 emails, 3 SMS sent"
 *   { emailsSent: 7, smsSent: 0 } → "7 emails sent"
 *   { emailsSent: 7, smsSkippedReason: "..." } → "7 emails sent (SMS skipped — event >24h out)"
 * Falls back to a generic string if the payload is missing (older backend).
 */
function formatNotifySummary(data, t) {
  // Translation-aware. `t` is optional for back-compat with any
  // legacy callers that haven't been threaded through useTranslation.
  const tx = typeof t === 'function' ? t : (k) => k;
  // New coalesced/debounced path: the backend enqueues into a pending
  // digest and a cron flushes it after a short window, so parents get
  // one consolidated email per batch instead of one per edit.
  if (data?.queued) {
    const secs = Number(data?.flushInSecs ?? 0);
    if (secs > 0) {
      const mins = Math.max(1, Math.round(secs / 60));
      return tx('schedule.notifySummary.queuedWithMins', { mins });
    }
    return tx('schedule.notifySummary.queued');
  }
  // Legacy "send now" response (kept for back-compat with older bundles).
  const e = Number(data?.emailsSent ?? 0);
  const s = Number(data?.smsSent ?? 0);
  const skipped = data?.smsSkippedReason || null;
  if (!e && !s && !skipped) return tx('schedule.notifySummary.parentsNotified');
  const parts = [];
  if (e) parts.push(tx('schedule.notifySummary.emails', { count: e }));
  if (s) parts.push(tx('schedule.notifySummary.sms', { count: s }));
  const base = parts.length ? tx('schedule.notifySummary.sent', { parts: parts.join(', ') }) : tx('schedule.notifySummary.parentsNotified');
  // If SMS was intentionally skipped by the window gate, surface that so
  // coaches don't wonder why opted-in parents didn't get a text.
  if (skipped && !s) return tx('schedule.notifySummary.smsSkipped', { base });
  return base;
}

function resultBadge(event) {
  if ((event.eventType || 'game') !== 'game') return null;
  const { homeScore, awayScore, homeAway } = event;
  if (homeScore == null || awayScore == null) return null;
  const teamScore = homeAway === 'home' ? homeScore : awayScore;
  const oppScore  = homeAway === 'home' ? awayScore : homeScore;
  if (teamScore > oppScore) return { label: `W ${teamScore}–${oppScore}`, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  if (teamScore < oppScore) return { label: `L ${teamScore}–${oppScore}`, cls: 'bg-red-100 text-red-700 border-red-200' };
  return { label: `D ${teamScore}–${oppScore}`, cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' };
}

const INPUT_CLS =
  'w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-[#0d1f14] ' +
  'focus:outline-none focus:ring-2 focus:ring-[#b5f03d]/30 focus:border-[#b5f03d] transition-colors';

const SESSION_TYPE_MAP = {
  game: 'Game', practice: 'Practice', tournament: 'Tournament',
  meeting: 'Other', other: 'Other',
};

/* ── Recurring events ────────────────────────────────────────────────── */
// JS getDay(): 0=Sun, 1=Mon, …, 6=Sat. We render Monday-first for coaches.
const WEEKDAYS = [
  { day: 1, short: 'M'  },
  { day: 2, short: 'T'  },
  { day: 3, short: 'W'  },
  { day: 4, short: 'Th' },
  { day: 5, short: 'F'  },
  { day: 6, short: 'Sa' },
  { day: 0, short: 'Su' },
];
// Day-index → i18n key suffix. Used by the recurring-series preview to
// render the long-form weekday names (Monday, Tuesday, …) in the active
// language. Index matches JS Date.getDay() (0 = Sunday) so it can be
// looked up directly from `repeatDays` without re-mapping.
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MAX_RECURRING = 200; // hard cap on series size

/** Generate an array of Date objects — one per occurrence between startDate
 *  and untilDate (inclusive) whose weekday is in daysOfWeek (JS day nums). */
function generateWeeklyInstances(startDate, timeStr, daysOfWeek, untilDate) {
  const instances = [];
  if (!daysOfWeek.size) return instances;
  const [h, m] = timeStr.split(':').map(Number);
  const cur    = new Date(`${startDate}T00:00:00`);
  const end    = new Date(`${untilDate}T23:59:59`);
  while (cur <= end) {
    if (daysOfWeek.has(cur.getDay())) {
      const inst = new Date(cur);
      inst.setHours(h, m, 0, 0);
      instances.push(inst);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return instances;
}

/** Fields on a game doc that should propagate when "edit all future" is used.
 *  `date` is intentionally excluded — each instance keeps its own date; only
 *  the time-of-day is overlaid per-instance (handled separately). */
const PROPAGATED_FIELDS = [
  'eventType', 'opponent', 'title', 'homeAway',
  'fieldId', 'fieldName', 'fieldAddress', 'notes',
];

/* ── RsvpPill ────────────────────────────────────────────────────────── */
function RsvpPill({ counts, loading }) {
  const { t } = useTranslation();
  if (loading) return <span className="h-4 w-20 rounded-full bg-zinc-100 animate-pulse inline-block" />;
  if (!counts) return null;
  const { yes = 0, no = 0, total = 0 } = counts;
  const pending = total - yes - no;
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
      <span className="text-emerald-600 font-semibold">{yes} ✓</span>
      {no > 0 && <><span className="text-zinc-300">·</span><span className="text-red-500">{no} ✗</span></>}
      {pending > 0 && <><span className="text-zinc-300">·</span><span className="text-zinc-400">{pending} {t('schedule.rsvp.pending')}</span></>}
    </span>
  );
}

/* ── AddEditEventModal ───────────────────────────────────────────────── */
function AddEditEventModal({ seasonId, teamId, season, team, teamFields, editEvent, scope, onClose, onSaved }) {
  const { t } = useTranslation();
  const isEdit         = !!editEvent;
  const isSeriesEdit   = isEdit && !!editEvent.seriesId;
  const isFutureScope  = isSeriesEdit && scope === 'future';

  const [form, setForm] = useState(() => {
    if (editEvent) {
      const d  = toDate(editEvent.date);
      const de = editEvent.endDate ? toDate(editEvent.endDate) : null;
      // Pull endTime from `endsAt` if present (May 2026+ events) — falls
      // back to extracting from `endDate` for older multi-day tournament
      // events that stored their end-of-day timestamp there. Older single-
      // day events without any end timestamp render endTime as empty;
      // form validation will require the coach to fill it in on next save.
      const ea = editEvent.endsAt ? toDate(editEvent.endsAt) : null;
      const endTimeSrc = ea || de;
      return {
        eventType:    editEvent.eventType || 'game',
        opponent:     editEvent.opponent  || '',
        title:        editEvent.title     || '',
        date:         d  ? format(d,  'yyyy-MM-dd') : '',
        endDate:      de ? format(de, 'yyyy-MM-dd') : '',
        time:         d  ? format(d,  'HH:mm')      : '',
        endTime:      endTimeSrc ? format(endTimeSrc, 'HH:mm') : '',
        homeAway:     editEvent.homeAway  || 'home',
        fieldId:      editEvent.fieldId   || '',
        fieldName:    editEvent.fieldName || '',
        fieldAddress: editEvent.fieldAddress || null,
        fieldLat:     editEvent.fieldLat  ?? null,
        fieldLng:     editEvent.fieldLng  ?? null,
        fieldPlaceId: editEvent.fieldPlaceId || null,
        fieldIsIndoor: editEvent.fieldIsIndoor || false,
        notes:        editEvent.notes     || '',
      };
    }
    // ── New event: pre-fill location from the team's default home
    // field if the coach picked one in Team Profile. We only fill
    // when the saved field still exists in teamFields (handles the
    // case where the default was deleted from the venue library
    // without being unset on the team doc). Coach can still pick a
    // different field — this just stops them re-typing the same
    // venue every week.
    const defaultField = team?.defaultHomeFieldId
      ? teamFields.find(f => f.id === team.defaultHomeFieldId)
      : null;
    return {
      eventType: 'game', opponent: '', title: '', date: '', endDate: '',
      time: '', endTime: '', homeAway: 'home',
      fieldId:       defaultField?.id        || '',
      fieldName:     defaultField?.name      || '',
      fieldAddress:  defaultField?.address   || null,
      fieldLat:      defaultField?.lat       ?? null,
      fieldLng:      defaultField?.lng       ?? null,
      fieldPlaceId:  defaultField?.placeId   || null,
      fieldIsIndoor: defaultField?.isIndoor  || false,
      notes: '',
    };
  });

  // Auto-suggest a sensible end time when the coach picks a start time,
  // unless they've already entered one. Default duration depends on event
  // type — practices and games typically run 90 min, tournaments are
  // multi-day so we don't auto-fill (coach picks an end day separately).
  // Coach can override anytime; this just removes the dead tap.
  useEffect(() => {
    if (!form.time || form.endTime) return;
    if (form.eventType === 'tournament') return;
    const [h, m] = form.time.split(':').map(Number);
    if (Number.isNaN(h)) return;
    const defaultMin = form.eventType === 'meeting' ? 60 : 90;
    const total = h * 60 + m + defaultMin;
    const eh = Math.floor(total / 60) % 24;
    const em = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    setForm(p => ({ ...p, endTime: `${pad(eh)}:${pad(em)}` }));
  }, [form.time, form.eventType]); // intentionally omit form.endTime so we don't fight user edits

  // Recurring state — only meaningful when creating new
  const [repeatOn,    setRepeatOn]    = useState(false);
  const [repeatDays,  setRepeatDays]  = useState(() => new Set()); // JS day nums (0-6)
  const [repeatUntil, setRepeatUntil] = useState('');

  // Auto-check the picked date's weekday when repeat is toggled on
  useEffect(() => {
    if (!repeatOn || !form.date) return;
    const wd = new Date(`${form.date}T00:00:00`).getDay();
    setRepeatDays(prev => prev.has(wd) ? prev : new Set([...prev, wd]));
  }, [repeatOn, form.date]);

  // Default the "until" date to the season's end date, if available
  useEffect(() => {
    if (!repeatOn || repeatUntil || !season) return;
    const seasonEnd = toDate(season.endDate);
    if (seasonEnd) setRepeatUntil(format(seasonEnd, 'yyyy-MM-dd'));
  }, [repeatOn, season, repeatUntil]);

  const [saving, setSaving]        = useState(false);
  const [notifyParents, setNotify] = useState(isEdit);

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));
  const isGame       = form.eventType === 'game';
  const isTournament = form.eventType === 'tournament';
  const typeInfo     = getTypeInfo(form.eventType);
  const selectedField = teamFields.find(f => f.id === form.fieldId) || null;

  const toggleDay = (day) => {
    setRepeatDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day); else next.add(day);
      return next;
    });
  };

  // Preview instances (for the helper caption under the repeat UI)
  const previewInstances = useMemo(() => {
    if (!repeatOn || !form.date || !form.time || !repeatUntil || !repeatDays.size) return [];
    return generateWeeklyInstances(form.date, form.time, repeatDays, repeatUntil);
  }, [repeatOn, form.date, form.time, repeatUntil, repeatDays]);

  const showRepeatUI = !isEdit && !isTournament;

  const handleFieldPick = (e) => {
    const id    = e.target.value;
    const field = teamFields.find(f => f.id === id);
    setForm(p => ({
      ...p,
      fieldId:      id,
      fieldName:    field?.name     || '',
      fieldAddress: field?.address  || null,
      fieldLat:     field?.lat      ?? null,
      fieldLng:     field?.lng      ?? null,
      fieldPlaceId: field?.placeId  || null,
      fieldIsIndoor: field?.isIndoor || false,
    }));
  };

  // Called by GooglePlacesInput when the coach picks a result from autocomplete
  const handlePlacePick = useCallback((place) => {
    if (!place) {
      setForm(p => ({
        ...p,
        fieldId: '', fieldName: '', fieldAddress: null,
        fieldLat: null, fieldLng: null, fieldPlaceId: null, fieldIsIndoor: false,
      }));
      return;
    }
    setForm(p => ({
      ...p,
      fieldId:      '',               // clear preset dropdown — they typed a new place
      fieldName:    place.name    || place.address || '',
      fieldAddress: place.address || null,
      fieldLat:     place.lat     ?? null,
      fieldLng:     place.lng     ?? null,
      fieldPlaceId: place.placeId || null,
      fieldIsIndoor: false,           // Places API doesn't tell us; default outdoor
    }));
  }, []);

  const save = async () => {
    if (isGame && !form.opponent.trim()) { toast.error(t('schedule.toast.opponentRequired')); return; }
    if (!isGame && !form.title.trim())   { toast.error(t('schedule.toast.titleRequired')); return; }
    if (!form.date)                       { toast.error(t('schedule.toast.dateRequired')); return; }
    if (!form.time)                       { toast.error(t('schedule.toast.startTimeRequired')); return; }
    if (!form.endTime)                    { toast.error(t('schedule.toast.endTimeRequired')); return; }
    // For single-day events, end time must come after start time. For
    // tournaments (which can span multiple days via form.endDate),
    // we don't validate endTime > time because the end is on a later
    // date — the multi-day check below catches inverted ranges.
    if (!isTournament && form.endTime <= form.time) {
      toast.error(t('schedule.toast.endAfterStart'));
      return;
    }

    // Repeat-mode validation (create flow only)
    if (showRepeatUI && repeatOn) {
      if (!repeatDays.size)                            { toast.error(t('schedule.toast.pickAtLeastOneDay')); return; }
      if (!repeatUntil)                                { toast.error(t('schedule.toast.pickEndDate')); return; }
      if (new Date(repeatUntil) < new Date(form.date)) { toast.error(t('schedule.toast.endAfterStartDate')); return; }
      if (previewInstances.length === 0)               { toast.error(t('schedule.toast.noEventsInRange')); return; }
      if (previewInstances.length > MAX_RECURRING) {
        toast.error(t('schedule.toast.tooManyEvents', { count: previewInstances.length, max: MAX_RECURRING }));
        return;
      }
    }

    setSaving(true);
    try {
      const kickoff = new Date(`${form.date}T${form.time}:00`);
      if (isNaN(kickoff.getTime())) { toast.error(t('schedule.toast.invalidDateTime')); setSaving(false); return; }

      // End timestamp: for single-day events the end is on the same date
      // as the start; for tournaments (multi-day) it's on form.endDate.
      // Both use form.endTime as the end clock-time.
      const endDateForTimestamp = (isTournament && form.endDate) ? form.endDate : form.date;
      const endsAt = new Date(`${endDateForTimestamp}T${form.endTime}:00`);
      if (isNaN(endsAt.getTime())) { toast.error(t('schedule.toast.invalidEndTime')); setSaving(false); return; }
      if (endsAt <= kickoff) {
        toast.error(t('schedule.toast.endMustBeAfterStart'));
        setSaving(false);
        return;
      }

      // Merge location: GooglePlacesInput pick > preset field > existing editEvent value
      const fieldAddress  = form.fieldAddress  || selectedField?.address  || editEvent?.fieldAddress  || null;
      const fieldLat      = form.fieldLat      ?? selectedField?.lat      ?? null;
      const fieldLng      = form.fieldLng      ?? selectedField?.lng      ?? null;
      const fieldPlaceId  = form.fieldPlaceId  || selectedField?.placeId  || null;
      const fieldIsIndoor = form.fieldIsIndoor ?? selectedField?.isIndoor ?? false;
      const fieldZip      = extractZip(fieldAddress);

      // Weather-coverage guardrail: the weather alert function silently
      // skips sessions without lat/lng or marked indoor. If the coach
      // typed a venue but didn't pick from the Places autocomplete (or
      // chose a legacy preset that lacks coordinates), the event will
      // never get a weather alert and the coach won't know. Surface
      // that BEFORE the write. See features/event-weather-coverage-guard/.
      const hasVenueWithoutCoords =
        (form.fieldName.trim() || fieldAddress) && // venue named/addressed
        !fieldIsIndoor &&                          // not indoor (coverage moot)
        (fieldLat == null || fieldLng == null);    // missing coords
      if (hasVenueWithoutCoords) {
        const proceed = window.confirm(t('schedule.toast.noWeatherCoverageConfirm'));
        if (!proceed) {
          setSaving(false);
          return;
        }
      }

      const payload = {
        seasonId,
        teamId,
        eventType:    form.eventType,
        date:         kickoff,
        endsAt:       endsAt,         // event end timestamp (added May 2026)
        fieldId:      form.fieldId   || null,
        fieldName:    form.fieldName.trim() || selectedField?.name || '',
        fieldAddress,
        fieldLat,
        fieldLng,
        fieldPlaceId,
        fieldIsIndoor,
        fieldZip,
        notes:        form.notes.trim() || null,
        updatedAt:    serverTimestamp(),
      };

      if (isGame) {
        payload.opponent = form.opponent.trim();
        payload.homeAway = form.homeAway;
        payload.title    = null;
      } else {
        payload.title    = form.title.trim();
        payload.opponent = null;
        payload.homeAway = null;
      }

      payload.endDate = (isTournament && form.endDate)
        ? new Date(`${form.endDate}T23:59:00`)
        : null;

      const sessionTitle = isGame ? `vs ${form.opponent.trim()}` : form.title.trim();
      const sessionType  = SESSION_TYPE_MAP[form.eventType] || 'Game';
      const locName      = form.fieldName.trim() || selectedField?.name || null;
      const locationPayload = locName
        ? {
            name:      locName,
            address:   fieldAddress   || null,
            lat:       fieldLat       ?? null,
            lng:       fieldLng       ?? null,
            zip:       fieldZip       || null,
            placeId:   fieldPlaceId   || null,
            isIndoor:  fieldIsIndoor,
          }
        : null;

      /* ═════════════════════════════════════════════════════════════
       *  PATH A — Create a new recurring series
       * ═════════════════════════════════════════════════════════════ */
      if (!isEdit && repeatOn && showRepeatUI) {
        const seriesId = doc(collection(db, 'games')).id;
        const batch    = writeBatch(db);

        previewInstances.forEach((instDate, i) => {
          const eventRef   = doc(collection(db, 'games'));
          const sessionRef = doc(collection(db, 'sessions'));

          // Compute per-instance endsAt: same end clock-time, applied to
          // this instance's date. Recurring weekly events are by definition
          // single-day, so endsAt is just (instance date) + (form.endTime).
          const [eh, em] = form.endTime.split(':').map(Number);
          const instEndsAt = new Date(instDate);
          instEndsAt.setHours(eh, em, 0, 0);

          batch.set(eventRef, {
            ...payload,
            date:                instDate,
            endsAt:              instEndsAt,
            status:              'scheduled',
            seriesId,
            seriesIndex:         i,
            attendanceSessionId: sessionRef.id,
            createdAt:           serverTimestamp(),
          });
          batch.set(sessionRef, {
            scheduleEventId: eventRef.id,
            seasonId,
            teamId,
            type:      sessionType,
            title:     sessionTitle,
            date:      instDate,
            time:      null,
            location:  locationPayload,
            note:      null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();

        toast.success(t('schedule.toast.createdSeries', { count: previewInstances.length }));
        onSaved();
        onClose();
        return;
      }

      /* ═════════════════════════════════════════════════════════════
       *  PATH B — Edit "this and all future events in series"
       * ═════════════════════════════════════════════════════════════ */
      if (isFutureScope) {
        const anchorDate = toDate(editEvent.date);

        // Find all future events (same series, date >= anchor).
        // Must include teamId to satisfy the games rule under Phase 2 —
        // a seriesId-only query fails permission-denied (the rule wraps
        // teamId in a function call which isn't pattern-matchable).
        const q = query(
          collection(db, 'games'),
          where('seriesId', '==', editEvent.seriesId),
          where('teamId',   '==', editEvent.teamId),
        );
        const snap = await getDocs(q);
        const futureEvents = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => toDate(e.date).getTime() >= anchorDate.getTime());

        if (futureEvents.length === 0) {
          toast.error(t('schedule.toast.noFutureEvents'));
          setSaving(false);
          return;
        }

        const batch       = writeBatch(db);
        const newH        = kickoff.getHours();
        const newM        = kickoff.getMinutes();
        const propagated  = {};
        PROPAGATED_FIELDS.forEach(f => { if (payload[f] !== undefined) propagated[f] = payload[f]; });

        futureEvents.forEach(ev => {
          // Keep each instance's date, but overlay the new time-of-day
          const evDate = toDate(ev.date);
          evDate.setHours(newH, newM, 0, 0);

          batch.update(doc(db, 'games', ev.id), {
            ...propagated,
            date:      evDate,
            updatedAt: serverTimestamp(),
          });

          // Update linked session if present
          if (ev.attendanceSessionId) {
            batch.update(doc(db, 'sessions', ev.attendanceSessionId), {
              type:      sessionType,
              title:     sessionTitle,
              date:      evDate,
              location:  locationPayload,
              updatedAt: serverTimestamp(),
            });
          }
        });
        await batch.commit();

        if (notifyParents) {
          try {
            const fn = httpsCallable(getFunctions(), 'sendGameUpdateSms');
            // Series reschedule: one email + one SMS per parent covering all
            // future events in the series, instead of N separate messages.
            // The backend uses `editEvent.id` (the anchor / first moved event)
            // as the reference date for its 24h SMS window gate.
            const res = await fn({
              gameId:     editEvent.id,
              changeType: 'reschedule',
              isSeries:   true,
            });
            toast.success(t('schedule.toast.eventsUpdatedWithSummary', { count: futureEvents.length, summary: formatNotifySummary(res?.data, t) }));
          } catch {
            toast.success(t('schedule.toast.eventsUpdated', { count: futureEvents.length }));
            toast.error(t('schedule.toast.parentNotifyFailed'));
          }
        } else {
          toast.success(t('schedule.toast.seriesUpdated', { count: futureEvents.length }));
        }

        onSaved();
        onClose();
        return;
      }

      /* ═════════════════════════════════════════════════════════════
       *  PATH C — Create a single event  OR  edit a single event
       * ═════════════════════════════════════════════════════════════ */
      let eventId;
      if (isEdit) {
        await updateDoc(doc(db, 'games', editEvent.id), payload);
        eventId = editEvent.id;
      } else {
        payload.status    = 'scheduled';
        payload.createdAt = serverTimestamp();
        const ref = await addDoc(collection(db, 'games'), payload);
        eventId = ref.id;
      }

      /* ── Sync linked attendance session ── */
      try {
        if (isEdit && editEvent.attendanceSessionId) {
          await updateDoc(doc(db, 'sessions', editEvent.attendanceSessionId), {
            type:      sessionType,
            title:     sessionTitle,
            date:      kickoff,
            location:  locationPayload,
            updatedAt: serverTimestamp(),
          });
        } else if (!isEdit) {
          const sessionRef = await addDoc(collection(db, 'sessions'), {
            scheduleEventId: eventId,
            seasonId,
            teamId,
            type:      sessionType,
            title:     sessionTitle,
            date:      kickoff,
            time:      null,
            location:  locationPayload,
            note:      null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          await updateDoc(doc(db, 'games', eventId), { attendanceSessionId: sessionRef.id });
        }
      } catch (sessionErr) {
        console.warn('[SeasonSchedule] session sync failed:', sessionErr);
      }

      const localizedTypeLabel = t(`schedule.eventType.${form.eventType || 'game'}`);
      if (isEdit && notifyParents) {
        try {
          const fn = httpsCallable(getFunctions(), 'sendGameUpdateSms');
          const res = await fn({ gameId: eventId, changeType: 'reschedule' });
          toast.success(t('schedule.toast.typeUpdatedWithSummary', { type: localizedTypeLabel, summary: formatNotifySummary(res?.data, t) }));
        } catch {
          toast.success(t('schedule.toast.typeUpdated', { type: localizedTypeLabel }));
          toast.error(t('schedule.toast.parentNotifyFailed'));
        }
      } else {
        toast.success(isEdit
          ? t('schedule.toast.typeUpdatedBang', { type: localizedTypeLabel })
          : t('schedule.toast.typeAdded', { type: localizedTypeLabel }));
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('[SeasonSchedule] save failed', err);
      toast.error(t('schedule.toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const titlePlaceholder =
    form.eventType === 'tournament' ? t('schedule.form.titlePlaceholderTournament') :
    form.eventType === 'practice'   ? t('schedule.form.titlePlaceholderPractice') :
    form.eventType === 'meeting'    ? t('schedule.form.titlePlaceholderMeeting') : t('schedule.form.titlePlaceholderDefault');

  const titleLabel =
    form.eventType === 'tournament' ? t('schedule.form.tournamentName') :
    form.eventType === 'meeting'    ? t('schedule.form.meetingTitle') : t('schedule.form.title');

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h2 className="text-base font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}>
            {isEdit ? t('schedule.editEventTitle') : t('schedule.addEventTitle')}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#b5f03d]/10 text-zinc-400 hover:text-[#0d1f14] transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Event type pill selector */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1.5"
              style={{ fontFamily: HEAD_FONT }}>
              {t('schedule.eventType.label')}
            </label>
            <div className="grid grid-cols-5 gap-1 p-1 bg-[#f7faf5] border border-zinc-200 rounded-xl">
              {EVENT_TYPES.map(et => (
                <button key={et.value} type="button"
                  onClick={() => setForm(p => ({ ...p, eventType: et.value }))}
                  className={clsx(
                    'flex flex-col items-center gap-0.5 rounded-lg py-1.5 text-[10px] font-bold uppercase transition-all leading-tight',
                    form.eventType === et.value
                      ? 'bg-[#0d1f14] text-white shadow-sm'
                      : 'text-zinc-500 hover:text-[#0d1f14]'
                  )}
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                  <span className="text-sm">{et.emoji}</span>
                  {t(`schedule.eventType.${et.value}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Opponent — game only */}
          {isGame && (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.opponent')}</label>
              <input type="text" value={form.opponent} onChange={set('opponent')}
                placeholder={t('schedule.form.opponentPlaceholder')} className={INPUT_CLS} />
            </div>
          )}

          {/* Title — non-game types */}
          {!isGame && (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                style={{ fontFamily: HEAD_FONT }}>{titleLabel}</label>
              <input type="text" value={form.title} onChange={set('title')}
                placeholder={titlePlaceholder} className={INPUT_CLS} />
            </div>
          )}

          {/* Date + start/end time. Mobile stacks vertically; desktop
              shows date full-width on row 1 and the two times side-by-
              side on row 2 so the coach can see the duration at a glance
              without having to scroll. End time auto-fills when start
              time changes (90 min default for game/practice, 60 for
              meeting); coach can override. */}
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.date')}</label>
              <input type="date" value={form.date} onChange={set('date')} className={INPUT_CLS} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                  style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.startTime')}</label>
                <input type="time" value={form.time} onChange={set('time')} className={INPUT_CLS} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                  style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.endTime')}</label>
                <input type="time" value={form.endTime} onChange={set('endTime')} className={INPUT_CLS} />
              </div>
            </div>
          </div>

          {/* Repeat — create flow, non-tournament only. Lives directly
              under the date/time fields (not at the bottom of the modal)
              so a coach planning "every Tuesday until November" sees it
              while thinking about dates. See
              features/recurring-events-discoverability/. */}
          {showRepeatUI && (
            <div className="rounded-xl border border-zinc-200 bg-[#f7faf5] p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={repeatOn} onChange={e => setRepeatOn(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#94cc28] focus:ring-[#b5f03d]/40" />
                <div className="flex-1">
                  <p className="text-sm font-bold text-[#0d1f14] flex items-center gap-1.5">
                    <ArrowPathRoundedSquareIcon className="h-4 w-4 text-[#5a8a1f]" />
                    {t('schedule.form.repeatWeekly')}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {t('schedule.form.repeatHelper')}
                  </p>
                </div>
              </label>

              {repeatOn && (
                <div className="pl-7 space-y-3">
                  {/* Day-of-week pills */}
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1.5"
                      style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.repeatOn')}</label>
                    <div className="flex gap-1 flex-wrap">
                      {WEEKDAYS.map(w => {
                        const active = repeatDays.has(w.day);
                        return (
                          <button key={w.day} type="button" onClick={() => toggleDay(w.day)}
                            className={clsx(
                              'w-9 h-9 rounded-full text-xs font-bold transition-colors border',
                              active
                                ? 'bg-[#0d1f14] text-white border-[#0d1f14]'
                                : 'bg-white text-zinc-500 border-zinc-200 hover:border-[#b5f03d]'
                            )}>
                            {w.short}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Until date — required when repeatOn is true. Without
                      this, the series has no upper bound and would either
                      generate an unbounded number of instances or silently
                      fail validation only on submit. The asterisk + helper
                      text signals upfront that it's required. */}
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                      style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.endsOn')}</label>
                    <input type="date" value={repeatUntil}
                      onChange={e => setRepeatUntil(e.target.value)}
                      min={form.date || undefined}
                      className={INPUT_CLS} />
                    {!repeatUntil && (
                      <p className="mt-1 text-[10px] text-zinc-500">
                        {t('schedule.form.endsOnHelper')}
                      </p>
                    )}
                  </div>

                  {/* Preview */}
                  {previewInstances.length > 0 && (
                    <div className="rounded-lg bg-[#b5f03d]/15 border border-[#b5f03d]/30 px-3 py-2 text-xs text-[#3d6010]">
                      <p className="font-bold">
                        {t('schedule.form.willCreate', { count: previewInstances.length })}
                      </p>
                      <p className="text-[#5a8a1f] mt-0.5">
                        {[...repeatDays].sort().map(d => t(`schedule.dayLong.${DAY_KEYS[d]}`)).join(', ')} · {form.date ? format(new Date(`${form.date}T00:00:00`), 'MMM d') : '—'} to {repeatUntil ? format(new Date(`${repeatUntil}T00:00:00`), 'MMM d, yyyy') : '—'}
                      </p>
                      {previewInstances.length > MAX_RECURRING && (
                        <p className="text-red-600 font-semibold mt-1">
                          {t('schedule.form.overLimit', { max: MAX_RECURRING })}
                        </p>
                      )}
                    </div>
                  )}
                  {previewInstances.length === 0 && form.date && repeatUntil && repeatDays.size > 0 && (
                    <p className="text-xs text-red-500">{t('schedule.form.noMatchingDays')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* End date — tournament only */}
          {isTournament && (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                style={{ fontFamily: HEAD_FONT }}>
                {t('schedule.form.endDate')} <span className="text-zinc-400 font-normal normal-case tracking-normal">{t('schedule.form.endDateHint')}</span>
              </label>
              <input type="date" value={form.endDate} onChange={set('endDate')}
                min={form.date} className={INPUT_CLS} />
            </div>
          )}

          {/* Home / Away — game only */}
          {isGame && (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
                style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.homeOrAway')}</label>
              <div className="flex rounded-lg border border-zinc-200 overflow-hidden">
                {['home', 'away'].map(opt => (
                  <button key={opt} type="button"
                    onClick={() => setForm(p => ({ ...p, homeAway: opt }))}
                    className={clsx('flex-1 py-2 text-sm font-bold uppercase transition-colors',
                      form.homeAway === opt ? 'bg-[#0d1f14] text-white' : 'bg-white text-zinc-500 hover:bg-[#b5f03d]/10'
                    )}
                    style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                    {t(`schedule.form.${opt}`)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Field / Venue */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
              style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.locationVenue')}</label>
            {teamFields.length > 0 && (
              <select value={form.fieldId} onChange={handleFieldPick} className={clsx(INPUT_CLS, 'mb-2')}>
                <option value="">{t('schedule.form.pickFromSaved')}</option>
                {teamFields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            )}
            {/* GooglePlacesInput — re-mounts when preset changes so defaultValue stays in sync */}
            <GooglePlacesInput
              key={form.fieldId || '_free'}
              defaultValue={form.fieldId ? '' : (form.fieldAddress || form.fieldName)}
              placeholder={teamFields.length > 0 ? t('schedule.form.searchOrPick') : t('schedule.form.searchVenue')}
              onPlace={handlePlacePick}
            />
            {/* Address line: show from Places pick, preset field, or saved editEvent */}
            {(form.fieldAddress || selectedField?.address) && (
              <p className="mt-1 text-xs text-zinc-400 flex items-center gap-1">
                <MapPinIcon className="h-3 w-3" />
                {form.fieldAddress || selectedField?.address}
              </p>
            )}
            {/* Coords confirmation — shows coaches that weather alerts will work */}
            {(form.fieldLat || selectedField?.lat) ? (
              <p className="mt-0.5 text-xs text-emerald-600 flex items-center gap-1">
                {t('schedule.form.coordinatesFound')}
              </p>
            ) : (form.fieldName || form.fieldAddress || selectedField?.address) ? (
              <p className="mt-0.5 text-xs text-amber-500 flex items-center gap-1">
                {t('schedule.form.selectFromDropdown')}
              </p>
            ) : null}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-1"
              style={{ fontFamily: HEAD_FONT }}>{t('schedule.form.notes')}</label>
            <textarea rows={2} value={form.notes} onChange={set('notes')}
              placeholder={t('schedule.form.notesPlaceholder')}
              className={clsx(INPUT_CLS, 'resize-none')} />
          </div>

          {/* Scope banner — editing a series instance with "all future" */}
          {isFutureScope && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-2.5">
              <ArrowPathRoundedSquareIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">{t('schedule.scope.editingThisAndFuture')}</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  {t('schedule.scope.editingThisAndFutureHelper')}
                </p>
              </div>
            </div>
          )}
          {isSeriesEdit && !isFutureScope && (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-start gap-2.5">
              <ArrowPathRoundedSquareIcon className="h-5 w-5 text-zinc-500 shrink-0 mt-0.5" />
              <p className="text-xs text-zinc-600">
                {t('schedule.scope.editingThisOnly')} <span className="font-semibold">{t('schedule.scope.editingThisOnlyEmphasis')}</span>{t('schedule.scope.editingThisOnlyTrailer')}
              </p>
            </div>
          )}

          {/* Notify parents checkbox — edit only */}
          {isEdit && (
            <label className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 cursor-pointer">
              <input type="checkbox" checked={notifyParents} onChange={e => setNotify(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-amber-500 focus:ring-amber-400" />
              <span className="text-sm text-amber-800">
                <span className="font-semibold">{t('schedule.notify.notifyParents')}</span> {t('schedule.notify.notifyParentsHelper')}
              </span>
            </label>
          )}

          {/* Hint — add only */}
          {!isEdit && (
            <p className="text-xs text-zinc-400 flex items-center gap-1.5">
              <BellAlertIcon className="h-3.5 w-3.5" />
              {t('schedule.notify.addHint')}
            </p>
          )}

          {/* Sticky action footer — the modal body scrolls (max-h-90vh)
              and Save used to live below the fold, invisible until the
              coach discovered there was more form to scroll (2026-07-24
              schedule audit). Sticky keeps the primary action on screen
              at every scroll position; negative margins span the body's
              p-5 padding so the bar reads as a true footer. */}
          <div className="sticky bottom-0 -mx-5 -mb-5 mt-2 px-5 py-4 bg-white border-t border-zinc-100 flex gap-2">
            <button onClick={onClose}
              className="flex-1 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              {t('schedule.cancel')}
            </button>
            <button onClick={save} disabled={saving}
              className="flex-1 inline-flex items-center justify-center rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>
              {saving
                ? t('schedule.saving')
                : isEdit
                  ? (isFutureScope ? t('schedule.updateAllFuture') : t('schedule.saveChanges'))
                  : (repeatOn && previewInstances.length > 0
                      ? t('schedule.createEvents', { count: previewInstances.length })
                      : t('schedule.addType', { type: t(`schedule.eventType.${form.eventType || 'game'}`) }))
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── ResultModal (games only) ────────────────────────────────────────── */
function ResultModal({ event, onClose, onSaved }) {
  const { t } = useTranslation();
  const isHome = event.homeAway === 'home';
  const [teamScore, setTeamScore] = useState(
    event.homeScore != null ? (isHome ? event.homeScore : event.awayScore) : ''
  );
  const [oppScore, setOppScore] = useState(
    event.awayScore != null ? (isHome ? event.awayScore : event.homeScore) : ''
  );
  const [shareViaSms, setShareViaSms] = useState(false);
  const [saving, setSaving]           = useState(false);

  const save = async () => {
    const ts = parseInt(teamScore, 10);
    const os = parseInt(oppScore,  10);
    if (isNaN(ts) || isNaN(os) || ts < 0 || os < 0) { toast.error(t('schedule.toast.validScores')); return; }

    setSaving(true);
    try {
      await updateDoc(doc(db, 'games', event.id), {
        homeScore: isHome ? ts : os,
        awayScore: isHome ? os : ts,
        status:    'completed',
        updatedAt: serverTimestamp(),
      });

      if (shareViaSms) {
        try {
          const fn = httpsCallable(getFunctions(), 'shareGameResult');
          await fn({ gameId: event.id });
          toast.success(t('schedule.toast.resultSavedSms'));
        } catch {
          toast.success(t('schedule.toast.resultSaved'));
          toast.error(t('schedule.toast.smsShareFailed'));
        }
      } else {
        toast.success(t('schedule.toast.resultSavedBang'));
      }

      onSaved();
      onClose();
    } catch {
      toast.error(t('schedule.toast.saveResultFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h2 className="text-base font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}>{t('schedule.enterResult')}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[#b5f03d]/10 text-zinc-400 hover:text-[#0d1f14] transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-zinc-500">{t('schedule.labels.vs')} {event.opponent}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-bold text-[#5a8a1f] mb-1 uppercase tracking-[1.4px]"
                style={{ fontFamily: HEAD_FONT }}>
                {isHome ? t('schedule.form.ourScore') : t('schedule.form.ourScoreAway')}
              </label>
              <input type="number" min="0" value={teamScore} onChange={e => setTeamScore(e.target.value)}
                className={clsx(INPUT_CLS, 'text-center text-2xl font-bold')} />
            </div>
            <div>
              <label className="block text-[11px] font-bold text-[#5a8a1f] mb-1 uppercase tracking-[1.4px]"
                style={{ fontFamily: HEAD_FONT }}>
                {event.opponent || t('schedule.form.opponentLabel')}
              </label>
              <input type="number" min="0" value={oppScore} onChange={e => setOppScore(e.target.value)}
                className={clsx(INPUT_CLS, 'text-center text-2xl font-bold')} />
            </div>
          </div>
          <label className="flex items-start gap-3 rounded-xl border border-[#b5f03d]/30 bg-[#b5f03d]/10 px-4 py-3 cursor-pointer">
            <input type="checkbox" checked={shareViaSms} onChange={e => setShareViaSms(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-[#94cc28] focus:ring-[#b5f03d]/40" />
            <span className="text-sm text-[#3d6010]">
              <span className="font-bold">{t('schedule.notify.shareViaSms')}</span> {t('schedule.notify.shareViaSmsHelper')}
            </span>
          </label>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              {t('schedule.cancel')}
            </button>
            <button onClick={save} disabled={saving}
              className="flex-1 inline-flex items-center justify-center rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>
              {saving ? t('schedule.saving') : t('schedule.saveResult')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SeriesScopeModal ────────────────────────────────────────────────
 * Asked before editing or cancelling a series instance.
 * ───────────────────────────────────────────────────────────────────── */
function SeriesScopeModal({ action, event, onClose, onConfirm }) {
  const { t } = useTranslation();
  const headerKey = action === 'edit' ? 'schedule.scope.editRecurring' : 'schedule.scope.cancelRecurring';
  const verbKey   = action === 'edit' ? 'schedule.scope.verbEdit'      : 'schedule.scope.verbCancel';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-3">
        <div className="flex items-start gap-3">
          <ArrowPathRoundedSquareIcon className="h-6 w-6 text-[#5a8a1f] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}>{t(headerKey)}</h3>
            <p className="text-sm text-zinc-500 mt-1">
              {t('schedule.scope.promptText', { verb: t(verbKey) })}
            </p>
          </div>
        </div>

        <div className="space-y-2 pt-1">
          <button onClick={() => onConfirm('single')}
            className="w-full text-left px-4 py-3 rounded-xl border border-zinc-200 bg-white hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors">
            <p className="text-sm font-bold text-[#0d1f14]">{t('schedule.scope.thisEventOnly')}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{t('schedule.scope.thisEventOnlyHelper')}</p>
          </button>
          <button onClick={() => onConfirm('future')}
            className="w-full text-left px-4 py-3 rounded-xl border border-zinc-200 bg-white hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors">
            <p className="text-sm font-bold text-[#0d1f14]">{t('schedule.scope.thisAndFuture')}</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {t('schedule.scope.thisAndFutureHelper')}
            </p>
          </button>
        </div>

        <button onClick={onClose}
          className="w-full py-2 text-sm font-bold uppercase text-zinc-500 hover:text-[#5a8a1f] transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
          {t('schedule.neverMind')}
        </button>
      </div>
    </div>
  );
}

/* ── CancelConfirm ───────────────────────────────────────────────────── */
function CancelConfirm({ event, scope, onClose, onCancelled, onCancelledCustomize }) {
  const { t } = useTranslation();
  const [cancelling, setCancelling] = useState(false);
  const [cancellingCustom, setCancellingCustom] = useState(false);
  const typeInfo    = getTypeInfo(event.eventType);
  const localizedTypeLabel = t(`schedule.eventType.${event.eventType || 'game'}`);
  const isFutureScope = scope === 'future' && event.seriesId;

  const eventName = (event.eventType || 'game') === 'game'
    ? null // game-vs case rendered with cancelGameVs key directly
    : (event.title || localizedTypeLabel);

  // Cancel WITHOUT firing the templated SMS — caller will open the smart
  // composer slide-over so the coach can write/AI-draft a custom message.
  // Useful for nuanced cancellations ("rain but we're rescheduling for
  // Tuesday — bring your indoor shoes") that the templated message can't
  // capture. The actual cancellation write is the same; the only diff is
  // that we skip the sendGameUpdateSms call.
  const cancelWithoutNotify = async () => {
    setCancellingCustom(true);
    try {
      if (isFutureScope) {
        const anchorDate = toDate(event.date);
        const snap = await getDocs(
          query(
            collection(db, 'games'),
            where('seriesId', '==', event.seriesId),
            where('teamId', '==', event.teamId),
          )
        );
        const futureEvents = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => toDate(e.date).getTime() >= anchorDate.getTime());
        const batch = writeBatch(db);
        futureEvents.forEach(e => {
          batch.update(doc(db, 'games', e.id), {
            status: 'cancelled', updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
        toast.success(t('schedule.toast.seriesEventsCancelledCustom', { count: futureEvents.length }));
      } else {
        await updateDoc(doc(db, 'games', event.id), {
          status: 'cancelled', updatedAt: serverTimestamp(),
        });
        toast.success(t('schedule.toast.typeCancelledCustom', { type: localizedTypeLabel }));
      }
      onCancelled();
      onClose();
      // Tell the parent to open the smart composer for this event with
      // messageType='Cancellation' (prefilled from the event).
      if (typeof onCancelledCustomize === 'function') {
        onCancelledCustomize(event);
      }
    } catch (err) {
      console.error('[SeasonSchedule] cancel-no-notify failed', err);
      toast.error(t('schedule.toast.cancelFailed'));
    } finally {
      setCancellingCustom(false);
    }
  };

  const confirm = async () => {
    setCancelling(true);
    try {
      if (isFutureScope) {
        // Cancel this + all future events in the series.
        //
        // Must also filter by teamId — the games rule gates on
        // isTeamStaff(resource.data.teamId), and Firestore's rules
        // engine can't statically prove a seriesId-only query is safe.
        // Without the teamId clause, this getDocs() fails permission-
        // denied under the Phase 2 lockdown (catch-all = if false).
        const anchorDate = toDate(event.date);
        const snap = await getDocs(
          query(
            collection(db, 'games'),
            where('seriesId', '==', event.seriesId),
            where('teamId', '==', event.teamId),
          )
        );
        const futureEvents = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(e => toDate(e.date).getTime() >= anchorDate.getTime());

        const batch = writeBatch(db);
        futureEvents.forEach(e => {
          batch.update(doc(db, 'games', e.id), {
            status:    'cancelled',
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();

        // Notify parents once per series (anchor event) — backend fans out
        // email to all parents + SMS to opt-ins. Series flag collapses wording
        // to "All future practices canceled" instead of N per-event messages,
        // and the 48h SMS window gate uses this anchor event's date.
        try {
          const fn = httpsCallable(getFunctions(), 'sendGameUpdateSms');
          const res = await fn({
            gameId:     event.id,
            changeType: 'cancellation',
            isSeries:   true,
          });
          toast.success(t('schedule.toast.seriesEventsCancelledSummary', { count: futureEvents.length, summary: formatNotifySummary(res?.data, t) }));
        } catch {
          toast.success(t('schedule.toast.seriesEventsCancelled', { count: futureEvents.length }));
          toast.error(t('schedule.toast.parentNotifyFailed'));
        }
      } else {
        // Single-instance cancel (existing behavior)
        await updateDoc(doc(db, 'games', event.id), { status: 'cancelled', updatedAt: serverTimestamp() });
        try {
          const fn = httpsCallable(getFunctions(), 'sendGameUpdateSms');
          const res = await fn({ gameId: event.id, changeType: 'cancellation' });
          toast.success(t('schedule.toast.typeCancelledSummary', { type: localizedTypeLabel, summary: formatNotifySummary(res?.data, t) }));
        } catch {
          toast.success(t('schedule.toast.typeCancelled', { type: localizedTypeLabel }));
          toast.error(t('schedule.toast.parentNotifyFailed'));
        }
      }

      onCancelled();
      onClose();
    } catch (err) {
      console.error('[SeasonSchedule] cancel failed', err);
      toast.error(t('schedule.toast.cancelFailed'));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="h-6 w-6 text-red-500 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold uppercase tracking-tight text-[#0d1f14] first-letter:uppercase"
              style={{ fontFamily: HEAD_FONT }}>
              {isFutureScope
                ? t('schedule.cancelModal.cancelSeries')
                : (event.eventType || 'game') === 'game'
                  ? t('schedule.cancelModal.cancelGameVs', { opponent: event.opponent || t('schedule.cancelModal.tbd') })
                  : t('schedule.cancelModal.cancelEvent', { name: eventName })}
            </h3>
            <p className="text-sm text-zinc-500 mt-1">
              {isFutureScope
                ? t('schedule.cancelModal.seriesHelper')
                : t('schedule.cancelModal.singleHelper')}
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 inline-flex items-center justify-center rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              {t('schedule.keepIt')}
            </button>
            <button onClick={confirm} disabled={cancelling || cancellingCustom}
              className="flex-1 inline-flex items-center justify-center rounded-xl bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>
              {cancelling ? t('schedule.cancelling') : t('schedule.yesCancelAndNotify')}
            </button>
          </div>
          {/* Secondary path — cancel WITHOUT firing the templated SMS, then
              open the AI composer so the coach can craft a custom message
              with reason + next-steps. Uses the same Phase 1 slide-over
              wired below. Series-level cancellations don't get this path
              yet because the templated cancellation already covers the
              series wording — would need a separate flow to compose one
              message that goes to all dates in the series. */}
          {!isFutureScope && (
            <button
              type="button"
              onClick={cancelWithoutNotify}
              disabled={cancelling || cancellingCustom}
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-[#b5f03d]/40 bg-[#b5f03d]/10 hover:bg-[#b5f03d]/20 text-[#3d6010] px-4 py-2 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
              title={t('schedule.card.cancelCustomTitle')}
            >
              {cancellingCustom
                ? t('schedule.cancelling')
                : t('schedule.cancelWriteCustom')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── EventCard ───────────────────────────────────────────────────────── */
function EventCard({ event, rsvpCounts, rsvpLoading, onEdit, onCancel, onNudge, onCardClick, attendanceSummary, canManage = true }) {
  const { t } = useTranslation();
  const kickoff     = toDate(event.date);
  const endDate     = event.endDate ? toDate(event.endDate) : null;
  const past        = kickoff ? isPast(kickoff) : false;
  const result      = resultBadge(event);
  const isCancelled = event.status === 'cancelled';
  const isGame      = (event.eventType || 'game') === 'game';
  const typeInfo    = getTypeInfo(event.eventType);
  const localizedTypeLabel = t(`schedule.eventType.${event.eventType || 'game'}`);
  const localizedTitle = isGame
    ? `${t('schedule.labels.vs')} ${event.opponent || t('schedule.cancelModal.tbd')}`
    : (event.title || localizedTypeLabel);

  // Smart-composer state — Phase 1 of the AI message redesign. Each
  // EventCard owns its own slide-over instance (cheap when closed —
  // returns null) so we can pre-fill from THIS event without lifting
  // a complex { activeEvent, messageType } pair up to the parent.
  const [composerType, setComposerType] = useState(null); // 'Reminder' | 'Game Recap' | null
  const openComposer = (type) => (e) => { e?.stopPropagation?.(); setComposerType(type); };
  const closeComposer = () => setComposerType(null);

  // Per-event nudge state — confirm-before-send + 5-minute cooldown
  // persisted across page refreshes via localStorage. Each card gets
  // its own cooldown keyed by its event ID.
  const nonResponderCount = rsvpCounts
    ? Math.max(0, rsvpCounts.total - rsvpCounts.yes - rsvpCounts.no)
    : 0;
  const nudgeCallable = useMemo(
    () => httpsCallable(getFunctions(), 'sendRsvpNudge'),
    []
  );
  const nudge = useNudgeCooldown(event.id, nonResponderCount, nudgeCallable);

  const handleNudge = async (e) => {
    e.stopPropagation();
    const data = await nudge.send();
    // Tell the parent to refresh RSVP counts so the new "yes/no"
    // responses (if any came in alongside the nudge) appear.
    if (data && typeof onNudge === 'function') onNudge(event.id);
  };

  const showNudge = canManage && !past && !isCancelled && nonResponderCount > 0;

  // Unified date-block treatment — lime accents for upcoming, soft grayscale for past/cancelled.
  const dateBlockCls = isCancelled
    ? 'bg-zinc-100'
    : past
      ? 'bg-zinc-50'
      : 'bg-[#b5f03d]/15';
  const monthCls = isCancelled ? 'text-zinc-300' : past ? 'text-zinc-400' : 'text-[#5a8a1f]';
  const dayCls   = isCancelled ? 'text-zinc-400' : past ? 'text-zinc-600' : 'text-[#0d1f14]';
  const dowCls   = isCancelled ? 'text-zinc-300' : past ? 'text-zinc-400' : 'text-[#5a8a1f]';

  // Whole card is clickable on BOTH upcoming + past non-cancelled events
  // (drills into EventDetailPage — that's where per-player RSVPs live).
  const cardClickable = !isCancelled && !!onCardClick;
  return (
    <div
      className={clsx(
        'rounded-2xl border bg-white px-5 py-4 transition-all shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]',
        isCancelled
          ? 'opacity-50 border-red-200'
          : past
            ? 'border-zinc-200'
            : 'border-zinc-200 hover:border-[#b5f03d]/40 hover:shadow-md',
        cardClickable && 'cursor-pointer',
      )}
      onClick={cardClickable ? onCardClick : undefined}
    >
      {/* Type pill — show for ALL events to harmonize with EventDetailPage */}
      {!isCancelled && (
        <div
          className="inline-flex items-center gap-1 bg-[#b5f03d]/15 text-[#3d6010] border border-[#b5f03d]/30 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full mb-2"
          style={{ fontFamily: HEAD_FONT }}
        >
          <span>{typeInfo.emoji}</span> {localizedTypeLabel}
        </div>
      )}

      <div className="flex items-start gap-3">
        {/* Date block */}
        <div className={clsx(
          'flex flex-col items-center justify-center rounded-xl px-3 py-2 min-w-[56px] text-center shrink-0',
          dateBlockCls
        )}>
          <span className={clsx('text-[10px] font-bold uppercase tracking-wide', monthCls)}
            style={{ fontFamily: HEAD_FONT }}>
            {kickoff ? format(kickoff, 'MMM') : '—'}
          </span>
          <span className={clsx('text-2xl font-black leading-tight', dayCls)}>
            {kickoff ? format(kickoff, 'd') : '—'}
          </span>
          <span className={clsx('text-[10px] font-semibold', dowCls)}>
            {kickoff ? format(kickoff, 'EEE') : ''}
          </span>
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-bold text-[#0d1f14] truncate">{localizedTitle}</span>

            {/* Series indicator */}
            {event.seriesId && (
              <span title={t('schedule.card.partOfSeries')}
                className="inline-flex items-center text-zinc-400">
                <ArrowPathRoundedSquareIcon className="h-3.5 w-3.5" />
              </span>
            )}

            {/* Game: home/away pill */}
            {isGame && event.homeAway && (
              <span className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border',
                event.homeAway === 'home'
                  ? 'bg-[#b5f03d]/15 text-[#3d6010] border-[#b5f03d]/30'
                  : 'bg-zinc-100 text-zinc-600 border-zinc-200'
              )}
                style={{ fontFamily: HEAD_FONT }}>
                {t(`schedule.form.${event.homeAway}`)}
              </span>
            )}

            {isCancelled && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-red-50 text-red-600 border border-red-200"
                style={{ fontFamily: HEAD_FONT }}>
                {t('schedule.labels.cancelled')}
              </span>
            )}
            {result && (
              <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border', result.cls)}>
                {result.label}
              </span>
            )}
          </div>

          {/* Time (+ end date for multi-day) */}
          {kickoff && (
            <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
              <ClockIcon className="h-3 w-3" />
              {fmtTime(kickoff)}
              {endDate && <span className="text-zinc-400 ml-1">– {format(endDate, 'MMM d')}</span>}
            </p>
          )}

          {/* Location */}
          {event.fieldName && (
            <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-1">
              <MapPinIcon className="h-3 w-3" />
              {event.fieldName}
              {event.homeAway === 'away' && event.fieldAddress && (
                <span className="text-zinc-400 ml-1">· {event.fieldAddress}</span>
              )}
            </p>
          )}
          {event.notes && <p className="text-xs text-zinc-500 mt-1 italic">{event.notes}</p>}

          {/* RSVP row — upcoming events */}
          {!isCancelled && !past && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <RsvpPill counts={rsvpCounts} loading={rsvpLoading} />
              {showNudge && (
                <button
                  onClick={handleNudge}
                  disabled={nudge.disabled}
                  className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors disabled:border-zinc-200 disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  title={nudge.cooldownRemaining > 0
                    ? t('schedule.card.cooldownTitle')
                    : t('schedule.card.nudgeTitle', { count: nonResponderCount })}
                >
                  <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
                  {nudge.label}
                </button>
              )}
              {/* Smart-composer: Send Reminder — opens a slide-over with
                  the event's date/time/location pre-filled and an AI-drafted
                  message ready for the coach to review + send. Distinct
                  from the RSVP nudge above (which targets non-responders
                  only with a "did you RSVP yet?" template). */}
              {canManage && (
                <button
                  onClick={openComposer('Reminder')}
                  className="inline-flex items-center gap-1 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/15 hover:bg-[#b5f03d]/25 text-[#3d6010] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  title={t('schedule.card.reminderTitle')}
                >
                  <SparklesIcon className="h-3.5 w-3.5" />
                  {t('schedule.card.sendReminder')}
                </button>
              )}
            </div>
          )}

          {/* Attendance summary — past events.
              Parents see the count if recorded, but never the
              "Take attendance →" CTA (coach-only action). */}
          {past && !isCancelled && (
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              {attendanceSummary ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                  <ClipboardDocumentListIcon className="h-3 w-3 text-zinc-400" />
                  <span className="text-emerald-600 font-semibold">{attendanceSummary.present} ✓</span>
                  <span className="text-zinc-300">·</span>
                  <span className="text-red-400">{attendanceSummary.total - attendanceSummary.present} ✗</span>
                </span>
              ) : canManage ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-[#5a8a1f] font-semibold">
                  <ClipboardDocumentListIcon className="h-3 w-3" />
                  {t('schedule.card.takeAttendance')}
                </span>
              ) : null}
              {/* Share Recap — past games with a posted score. Most data-
                  complete case for the smart composer (opponent, score,
                  date all auto-filled). Coach only adds the highlights
                  one-liner and hits send. */}
              {canManage && isGame && result && (
                <button
                  onClick={openComposer('Game Recap')}
                  className="inline-flex items-center gap-1 rounded-lg border border-[#b5f03d]/40 bg-[#b5f03d]/15 hover:bg-[#b5f03d]/25 text-[#3d6010] px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
                  title={t('schedule.card.recapTitle')}
                >
                  <SparklesIcon className="h-3.5 w-3.5" />
                  {t('schedule.card.shareRecap')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Action buttons — coach-only, upcoming events */}
        {canManage && !isCancelled && !past && (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={(e) => { e.stopPropagation(); onEdit(); }} title={t('schedule.card.editEvent')}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-[#5a8a1f] hover:bg-[#b5f03d]/10 transition-colors">
              <PencilSquareIcon className="h-4 w-4" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onCancel(); }} title={t('schedule.card.cancelEvent')}
              className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* Smart composer slide-over. Mounted but null when closed.
          Pre-fills from this event's data + auto-generates the AI message
          on open so the coach reviews + sends instead of filling out a form. */}
      <NotifyComposerSlideOver
        open={!!composerType}
        onClose={closeComposer}
        event={event}
        teamId={event?.teamId}
        messageType={composerType}
      />
    </div>
  );
}

/* ── SeasonSchedule ──────────────────────────────────────────────────── */
export default function SeasonSchedule() {
  const { t } = useTranslation();
  const { seasonId } = useParams();
  const navigate     = useNavigate();
  const location     = useLocation();
  const { userProfile } = useAuth();
  const { teamRole } = useRole();

  const [season,     setSeason]     = useState(null);
  const [teamFields, setTeamFields] = useState([]);
  // Team doc — needed for the new-event prefill so the modal can read
  // `defaultHomeFieldId`. Lazy-loaded once per team change.
  const [team,       setTeam]       = useState(null);
  const [events,     setEvents]     = useState([]);
  const [loading,    setLoading]    = useState(true);

  const [rsvpCounts,       setRsvpCounts]       = useState({});
  const [rsvpLoading,      setRsvpLoading]       = useState(false);
  const [attendanceCounts, setAttendanceCounts]  = useState({}); // { eventId: { present, total } }

  const [filterType,  setFilterType]  = useState('all');
  const [showAdd,     setShowAdd]     = useState(false);
  const [editEvent,   setEditEvent]   = useState(null);
  const [editScope,   setEditScope]   = useState('single');  // 'single' | 'future'
  const [cancelEvent, setCancelEvent] = useState(null);
  const [cancelScope, setCancelScope] = useState('single');
  const [scopePrompt, setScopePrompt] = useState(null);      // { action, event }
  const [showPast,    setShowPast]    = useState(false);

  // Smart-composer state at the schedule level — opens after the user
  // picks "Cancel + write custom message" in the cancel modal. The
  // EventCard owns its own composer state for Send Reminder / Share
  // Recap; this one is specifically for the cancellation flow because
  // the modal lives at the page level, not inside any single card.
  const [pageComposer, setPageComposer] = useState(null); // { event, messageType } | null

  const [rev, setRev] = useState(0);
  const bump = useCallback(() => setRev(r => r + 1), []);

  // Per-team role for THIS season's team. Parents see the schedule
  // but can't add/edit/cancel events, take attendance, or text
  // non-responders. canManage gates every coach-only control below
  // and on each EventCard. Resolves once season loads (we have the
  // teamId then); during loading the page renders a skeleton anyway.
  const isParent  = teamRole(season?.teamId) === 'parent';
  const canManage = !isParent;

  /* ── Load season + fields ── */
  useEffect(() => {
    getDoc(doc(db, 'seasons', seasonId)).then(snap => {
      if (snap.exists()) setSeason({ id: snap.id, ...snap.data() });
    });
  }, [seasonId]);

  useEffect(() => {
    if (!season?.teamId) return;
    getDocs(collection(db, 'teams', season.teamId, 'fields')).then(snap => {
      setTeamFields(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    // One-shot team fetch — we only need defaultHomeFieldId for the
    // new-event prefill, which doesn't change mid-session. A live
    // listener here would cost a read on every team write across the
    // whole app while the schedule page is open.
    getDoc(doc(db, 'teams', season.teamId)).then(snap => {
      if (snap.exists()) setTeam({ id: snap.id, ...snap.data() });
    }).catch(err => {
      console.warn('[SeasonSchedule] team fetch failed:', err?.code, err?.message);
    });
  }, [season?.teamId]);

  /* ── Live event list (Firestore collection is still 'games') ──
   *
   * Must filter by BOTH seasonId AND teamId. The Phase 2 lockdown of the
   * games rule gates reads on isTeamStaff(resource.data.teamId) and team
   * membership; Firestore's rules engine can't statically prove a
   * seasonId-only query is safe (the rule wraps teamId in function calls
   * which aren't pattern-matchable). Without the teamId clause this
   * listener fails permission-denied silently and the schedule page
   * gets stuck in its skeleton-loading state.
   *
   * Depends on `season?.teamId` so we wait for the season doc to load
   * before firing the query.
   */
  useEffect(() => {
    if (!seasonId || !season?.teamId) return;
    const q = query(
      collection(db, 'games'),
      where('seasonId', '==', seasonId),
      where('teamId', '==', season.teamId),
    );
    return onSnapshot(q, snap => {
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      if (import.meta.env.DEV) {
        console.warn('[SeasonSchedule] events subscribe failed', err);
      }
      setLoading(false);
    });
  }, [seasonId, season?.teamId]);

  /* ── Types present → filter chips ── */
  const presentTypes = useMemo(() => {
    const present = new Set(events.map(e => e.eventType || 'game'));
    return EVENT_TYPES.filter(et => present.has(et.value));
  }, [events]);

  /* ── Split upcoming / past (with type filter) ── */
  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const filtered = filterType === 'all'
      ? events
      : events.filter(e => (e.eventType || 'game') === filterType);
    const sorted = [...filtered].sort(
      (a, b) => (toDate(a.date)?.getTime() ?? 0) - (toDate(b.date)?.getTime() ?? 0)
    );
    return {
      upcoming: sorted.filter(e => e.status !== 'cancelled' && toDate(e.date) >= now),
      past:     sorted.filter(e => e.status === 'cancelled' || toDate(e.date) < now).reverse(),
    };
  }, [events, filterType]);

  /* ── RSVP counts for upcoming events ── */
  const upcomingIds = upcoming.map(e => e.id).join(',');
  useEffect(() => {
    if (!upcoming.length) { setRsvpCounts({}); return; }
    setRsvpLoading(true);
    Promise.all(
      upcoming.map(e =>
        getDocs(collection(db, 'games', e.id, 'rsvps')).then(snap => {
          let yes = 0, no = 0;
          snap.forEach(d => { if (d.data().response === 'yes') yes++; else no++; });
          return [e.id, { yes, no, total: snap.size }];
        })
      )
    ).then(entries => {
      setRsvpCounts(Object.fromEntries(entries));
      setRsvpLoading(false);
    }).catch(() => setRsvpLoading(false));
  }, [upcomingIds, rev]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Auto-open edit modal from navigation state (e.g. from EventDetailPage) ── */
  useEffect(() => {
    const editId = location.state?.openEditId;
    if (!editId || !events.length) return;
    const ev = events.find(e => e.id === editId);
    if (ev) {
      // Route through scope prompt if it's a series instance
      if (ev.seriesId) {
        setScopePrompt({ action: 'edit', event: ev });
      } else {
        setEditScope('single');
        setEditEvent(ev);
      }
      // Clear the navigation state so refreshing doesn't re-open
      window.history.replaceState({}, '');
    }
  }, [location.state?.openEditId, events]);

  /* ── Load attendance counts for past events ── */
  const pastEventIds = past.map(e => e.id).join(',');
  useEffect(() => {
    const pastWithSession = past.filter(e => e.attendanceSessionId);
    if (!pastWithSession.length) { setAttendanceCounts({}); return; }
    Promise.all(
      pastWithSession.map(e =>
        getDoc(doc(db, 'sessions', e.attendanceSessionId)).then(snap => {
          if (!snap.exists()) return null;
          const { presentCount = 0, attendanceCount = 0 } = snap.data();
          return [e.id, { present: presentCount, total: attendanceCount }];
        })
      )
    ).then(entries => {
      setAttendanceCounts(Object.fromEntries(entries.filter(Boolean)));
    }).catch(() => {/* non-fatal */});
  }, [pastEventIds]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Nudge handler ──
   * EventCard now owns the actual SMS send + toast + cooldown state via
   * useNudgeCooldown. This callback just refreshes RSVP counts after a
   * successful send so any newly-recorded responses appear without a
   * page refresh. (`bump()` increments a key that re-fetches RSVPs.)
   */
  const handleNudge = useCallback(() => {
    bump();
  }, [bump]);

  /* ── Navigate to event detail page ── */
  const handleCardClick = useCallback((eventId) => {
    navigate(`/seasons/${seasonId}/schedule/${eventId}`);
  }, [navigate, seasonId]);

  /* ── Edit / cancel routers (handle series scope prompt) ── */
  const requestEdit = useCallback((event) => {
    if (event.seriesId) {
      setScopePrompt({ action: 'edit', event });
    } else {
      setEditScope('single');
      setEditEvent(event);
    }
  }, []);

  const requestCancel = useCallback((event) => {
    if (event.seriesId) {
      setScopePrompt({ action: 'cancel', event });
    } else {
      setCancelScope('single');
      setCancelEvent(event);
    }
  }, []);

  const handleScopeChoice = useCallback((scope) => {
    if (!scopePrompt) return;
    if (scopePrompt.action === 'edit') {
      setEditScope(scope);
      setEditEvent(scopePrompt.event);
    } else {
      setCancelScope(scope);
      setCancelEvent(scopePrompt.event);
    }
    setScopePrompt(null);
  }, [scopePrompt]);

  /* ── Season record (games only) ── */
  const record = useMemo(() => {
    let w = 0, l = 0, d = 0;
    events.forEach(e => {
      if ((e.eventType || 'game') !== 'game' || e.status !== 'completed') return;
      const r = resultBadge(e);
      if (!r) return;
      if (r.label.startsWith('W')) w++;
      else if (r.label.startsWith('L')) l++;
      else d++;
    });
    return { w, l, d };
  }, [events]);

  const hasRecord = (record.w + record.l + record.d) > 0;

  /* ── Render ── */
  return (
    <div className="min-h-screen bg-[#f7faf5]">
      {/* Standardized container — same max-w-5xl + padding as ViewTeam,
          RosterPage, and TeamSeasonsList so the four team sub-pages
          feel like one consistent surface as the user tabs between them
          via TeamSubNav. Pre-fix this used max-w-2xl (672px) which felt
          cramped on desktop and inconsistent next to its sibling tabs. */}
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Team sub-nav: keeps Overview/Roster/Seasons/Schedule/Chat tabs visible while drilled into team-scoped content. */}
        <TeamSubNav teamId={season?.teamId} />
        {/* Header. Back arrow removed — TeamSubNav above already
            provides navigation to sibling tabs (Overview / Roster /
            Seasons / Chat), so a back button was redundant chrome.
            Pre-fix the back arrow navigated to /seasons/:id (SeasonDetail)
            which the user typically didn't come from anyway (they
            arrived via the Schedule tab → ScheduleGateway redirect),
            making the destination feel arbitrary. */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}>{t('schedule.title')}</h1>
            {season?.name && <p className="text-sm text-zinc-500 truncate">{season.name}</p>}
          </div>
          {canManage && (
            <button onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}>
              <PlusIcon className="h-4 w-4" />{t('schedule.addEvent')}
            </button>
          )}
        </div>

        {/* Season record pill (games only) */}
        {hasRecord && (
          <div className="mb-4">
            <div className="inline-flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-2.5 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
              <TrophyIcon className="h-4 w-4 text-[#5a8a1f]" />
              <span className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                style={{ fontFamily: HEAD_FONT }}>{t('schedule.seasonRecord')}</span>
              <div className="flex items-center gap-2 text-sm font-bold">
                <span className="text-emerald-600">{record.w}W</span>
                <span className="text-zinc-300">·</span>
                <span className="text-red-500">{record.l}L</span>
                {record.d > 0 && <><span className="text-zinc-300">·</span><span className="text-zinc-500">{record.d}D</span></>}
              </div>
            </div>
          </div>
        )}

        {/* Filter chips — only shown if multiple event types present */}
        {presentTypes.length > 1 && (
          <div className="flex items-center gap-2 mb-5 flex-wrap">
            <FunnelIcon className="h-3.5 w-3.5 text-[#5a8a1f] shrink-0" />
            <button
              onClick={() => setFilterType('all')}
              className={clsx(
                'px-3 py-1 rounded-full text-xs font-bold uppercase border transition-colors',
                filterType === 'all'
                  ? 'bg-[#0d1f14] text-white border-[#0d1f14]'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]'
              )}
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
              {t('schedule.all')}
            </button>
            {presentTypes.map(et => (
              <button key={et.value}
                onClick={() => setFilterType(et.value)}
                className={clsx(
                  'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase border transition-colors',
                  filterType === et.value
                    ? 'bg-[#0d1f14] text-white border-[#0d1f14]'
                    : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#b5f03d]'
                )}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>
                {et.emoji} {t(`schedule.eventTypePlural.${et.value}`)}
              </button>
            ))}
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(n => <div key={n} className="h-24 rounded-2xl bg-white border border-zinc-200 animate-pulse" />)}
          </div>
        )}

        {!loading && (
          <>
            {/* Empty — no events at all */}
            {events.length === 0 && (
              <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-10 text-center">
                <CalendarDaysIcon className="mx-auto h-10 w-10 text-[#b5f03d] mb-3" />
                <p className="text-sm font-bold uppercase text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}>{t('schedule.empty.noEventsYet')}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {canManage
                    ? t('schedule.empty.coachCta')
                    : t('schedule.empty.parentCta')}
                </p>
              </div>
            )}

            {/* Empty — filter has no results but events exist */}
            {events.length > 0 && upcoming.length === 0 && past.length === 0 && filterType !== 'all' && (
              <div className="rounded-2xl border-2 border-dashed border-zinc-200 bg-white p-8 text-center">
                <p className="text-sm text-zinc-500">
                  {t('schedule.labels.noTypeYet', { type: t(`schedule.eventType.${filterType}`).toLowerCase() })}
                </p>
              </div>
            )}

            {/* Upcoming — grouped by week with sticky headers.
                Each week's header sticks just below the mobile top bar
                (top-12 ≈ 48px) so the parent can keep scrolling and
                still know which week they're looking at. */}
            {upcoming.length > 0 && (
              <section className="mb-6">
                <h2 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3"
                  style={{ fontFamily: HEAD_FONT }}>
                  {t('schedule.upcoming')} · {sectionCount(upcoming.length, filterType, t)}
                </h2>
                <div className="space-y-6">
                  {groupByWeek(upcoming, t).map(group => (
                    <div key={group.weekStart.getTime()}>
                      <div
                        className="sticky top-12 md:top-2 z-10 -mx-1 px-1 py-2 mb-2 backdrop-blur-md bg-[#f7faf5]/80 border-b border-zinc-200/60"
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <h3 className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
                            style={{ fontFamily: HEAD_FONT }}>
                            {group.label}
                          </h3>
                          <span className="text-[10px] font-semibold text-zinc-400 tabular-nums">
                            {t('schedule.labels.eventCount', { count: group.events.length })}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-3">
                        {group.events.map(event => (
                          <EventCard
                            key={event.id}
                            event={event}
                            rsvpCounts={rsvpCounts[event.id] ?? null}
                            rsvpLoading={rsvpLoading}
                            onEdit={() => requestEdit(event)}
                            onCancel={() => requestCancel(event)}
                            onNudge={handleNudge}
                            onCardClick={() => handleCardClick(event.id)}
                            canManage={canManage}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Past & cancelled */}
            {past.length > 0 && (
              <section>
                <button onClick={() => setShowPast(p => !p)}
                  className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] mb-3 hover:text-[#3d6010] transition-colors"
                  style={{ fontFamily: HEAD_FONT }}>
                  {showPast ? <ChevronUpIcon className="h-3.5 w-3.5" /> : <ChevronDownIcon className="h-3.5 w-3.5" />}
                  {t('schedule.pastAndCancelled')} · {past.length}
                </button>
                {showPast && (
                  <div className="space-y-3">
                    {past.map(event => (
                      <EventCard
                        key={event.id}
                        event={event}
                        rsvpCounts={null}
                        rsvpLoading={false}
                        onEdit={() => requestEdit(event)}
                        onCancel={() => requestCancel(event)}
                        onNudge={handleNudge}
                        onCardClick={() => handleCardClick(event.id)}
                        attendanceSummary={attendanceCounts[event.id] ?? null}
                        canManage={canManage}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}

        {/* Modals */}
        {showAdd && (
          <AddEditEventModal seasonId={seasonId} teamId={season?.teamId}
            season={season} team={team} teamFields={teamFields} editEvent={null}
            onClose={() => setShowAdd(false)} onSaved={bump} />
        )}
        {editEvent && (
          <AddEditEventModal seasonId={seasonId} teamId={season?.teamId}
            season={season} team={team} teamFields={teamFields} editEvent={editEvent}
            scope={editScope}
            onClose={() => setEditEvent(null)} onSaved={bump} />
        )}
        {cancelEvent && (
          <CancelConfirm
            event={cancelEvent}
            scope={cancelScope}
            onClose={() => setCancelEvent(null)}
            onCancelled={bump}
            // Secondary path: cancel WITHOUT auto-notify, then open the
            // composer so the coach can write a custom cancellation
            // message. We also need a fresh-event lookup here because
            // the bump() above will refresh the live event list, but
            // the slide-over reads from the doc we have in hand.
            onCancelledCustomize={(ev) => {
              // Stamp status: 'cancelled' on the local copy so the
              // composer's prefill reflects the cancelled state. The
              // Cancellation messageType handles the rest.
              setPageComposer({
                event: { ...ev, status: 'cancelled' },
                messageType: 'Cancellation',
              });
            }}
          />
        )}

        {/* Page-level smart composer — opened by the cancel modal's
            "write custom message" path. Distinct from the per-card
            composer instances inside EventCard. */}
        {pageComposer && (
          <NotifyComposerSlideOver
            open={!!pageComposer}
            onClose={() => setPageComposer(null)}
            event={pageComposer.event}
            teamId={pageComposer.event?.teamId}
            messageType={pageComposer.messageType}
          />
        )}
        {scopePrompt && (
          <SeriesScopeModal
            action={scopePrompt.action}
            event={scopePrompt.event}
            onClose={() => setScopePrompt(null)}
            onConfirm={handleScopeChoice}
          />
        )}
      </div>
    </div>
  );
}
